import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';
import type { Contact } from '../../api/types';
import { useSaveContact } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { blankToNull } from '../common/format';
import { useErrorMessage } from '../common/Common';

export function ContactFormDialog({
  open, customerId, contact, onClose, onSaved,
}: {
  open: boolean;
  customerId: number;
  contact?: Contact | null;
  onClose: () => void;
  /** Lets a caller act on the saved contact — the opportunity form uses it to select the
   *  person who was just added, so adding a champion inline needs no second step. */
  onSaved?: (contact: Contact) => void;
}) {
  const { t } = useTranslation();
  const describeError = useErrorMessage();
  const save = useSaveContact();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(contact?.name ?? '');
    setEmail(contact?.email ?? '');
    setPhone(contact?.phone ?? '');
    setJobTitle(contact?.jobTitle ?? '');
    setNotes(contact?.notes ?? '');
    setTouched(false);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact]);

  const nameMissing = name.trim().length === 0;
  const serverErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const conflict = save.error instanceof ApiError && save.error.code === 'conflict';

  const submit = async () => {
    setTouched(true);
    if (nameMissing) return;
    const saved = await save.mutateAsync({
      id: contact?.id,
      body: {
        customerId,
        name: name.trim(),
        email: blankToNull(email),
        phone: blankToNull(phone),
        jobTitle: blankToNull(jobTitle),
        notes: blankToNull(notes),
      },
    });
    onSaved?.(saved);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{contact ? t('forms.contact.editTitle') : t('forms.contact.newTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {conflict && <Alert severity="warning">{describeError(save.error)}</Alert>}
          {save.error && !conflict && !Object.keys(serverErrors).length && (
            <Alert severity="error">{describeError(save.error)}</Alert>
          )}
          <TextField
            label={t('common.name')}
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            error={(touched && nameMissing) || Boolean(serverErrors.name)}
            helperText={touched && nameMissing ? t('forms.contact.nameRequired') : serverErrors.name}
            inputProps={{ dir: 'auto', maxLength: 200, 'data-testid': 'contact-name' }}
          />
          <TextField
            label={t('forms.contact.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(serverErrors.email)}
            helperText={serverErrors.email}
            inputProps={{ dir: 'auto', maxLength: 320, 'data-testid': 'contact-email' }}
          />
          <TextField
            label={t('forms.contact.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputProps={{ dir: 'auto', maxLength: 50, 'data-testid': 'contact-phone' }}
          />
          <TextField
            label={t('forms.contact.jobTitle')}
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            inputProps={{ dir: 'auto', maxLength: 150, 'data-testid': 'contact-job-title' }}
          />
          <TextField
            label={t('common.notes')}
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ dir: 'auto', 'data-testid': 'contact-notes' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={save.isPending}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending} data-testid="save-contact">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
